














import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import TwoStagesSelect, {
  TwoStagesModel,
  TwoStagesSelectGroup,
  TwoStagesSelectItems,
} from "@/components/TwoStagesSelect.vue";
import BroadcastersService from "@/services/SpecialInitiatives/SpecialInitiativesBroadcastersService";
import CompaniesService from "@/services/SpecialInitiatives/SpecialInitiativesCompaniesService";
import PublishersService from "@/services/SpecialInitiatives/SpecialInitiativesPublishersService";
import MarketSegmentsService from "@/services/SpecialInitiatives/SpecialInitiativesMarketSegmentsService";
import NamedIdentifier from "@/models/SpecialInitiatives/NamedIdentifier";

export type SubjectKind =
  | {
      id: "sales_agencies";
      name: "Concessionarie";
    }
  | {
      id: "broadcasters";
      name: "Emittenti";
    }
  | {
      id: "publishers";
      name: "Editori";
    }
  | {
      id: "market_segments";
      name: "Segmenti di mercato";
    }
  | null;

@Component({ components: { TwoStagesSelect } })
export default class SpecialInitiativesSubjectSelector extends Vue {
  @Prop({ default: null }) readonly selectedKind!: SubjectKind;
  @Prop({ default: [] }) readonly value!: string[];
  loading = false;
  model: TwoStagesModel = {
    selectedGroupId: null,
    groups: [],
  };

  availableSubjectKinds: SubjectKind[] = [
    {
      id: "sales_agencies",
      name: "Concessionarie",
    },
    {
      id: "broadcasters",
      name: "Emittenti",
    },
    {
      id: "publishers",
      name: "Editori",
    },
    {
      id: "market_segments",
      name: "Segmenti di mercato",
    },
  ];

  @Watch("selectedKind", { deep: true })
  onSelectedKindChanged(value: SubjectKind) {
    if (value) {
      this.model.selectedGroupId = value.id;
    }
  }

  @Watch("value", { deep: true })
  onPropValueChanged(value: string[]) {
    if (value) {
      const group = this.model.groups.find(
        (group) => group.id === this.model.selectedGroupId
      );
      if (group) {
        let sum = 0;
        group.items.forEach((item) => {
          const selected = !!value.find((element) => element === item.id);
          if (selected) {
            sum++;
            item.selected = true;
          }
        });
        group.count = sum;
      }
    }
  }

  async mounted() {
    this.model = await this.initModel();
  }

  async onGroupSelected(group: TwoStagesSelectGroup, model: TwoStagesModel) {
    this.model = JSON.parse(JSON.stringify(model));
    const kind: SubjectKind =
      (this.availableSubjectKinds.find(
        (kind) => kind!.id === group.id
      ) as SubjectKind) || null;
    if (kind) {
      this.model.selectedGroupId = kind.id;
      const group = this.model.groups.find((group) => group.id === kind.id);
    }
  }

  onValueChanged(model: TwoStagesModel) {
    this.model = JSON.parse(JSON.stringify(model));
    const group =
      this.model.groups.find((group) => group.id === model.selectedGroupId) ||
      null;
    if (group) {
      this.$emit(
        "input",
        group.items.reduce((acc, item) => {
          if (item.selected) {
            acc.push(item.id);
          }
          return acc;
        }, [] as string[])
      );
      this.$emit(
        "kind",
        this.availableSubjectKinds.find(
          (kind) => kind!.id === model.selectedGroupId
        ) || null
      );
    }
  }

  onClosed() {
    if (this.selectedKind) {
      const selectedKind = this.selectedKind;
      this.model.selectedGroupId = selectedKind.id;
      const group =
        this.model.groups.find((group) => group.id === selectedKind.id) || null;
      if (group) {
        group.count = this.value.length;
        group.items.forEach((item) => {
          item.selected = !!this.value.find((element) => element === item.id);
        });
      }
    }
  }

  async initModel(): Promise<TwoStagesModel> {
    let selectedKindId = null;
    if (this.selectedKind) {
      selectedKindId = this.selectedKind.id;
    }
    const groups = await Promise.all(
      this.availableSubjectKinds.map(async (kind) => {
        const items = (await this.loadItems(kind)) || [];
        return {
          id: kind!.id,
          text: kind!.name,
          count: this.value.length,
          items: items
            .sort((a, b) => {
              if (a.name < b.name) {
                return -1;
              } else if (a.name > b.name) {
                return 1;
              } else {
                return 0;
              }
            })
            .map((element) => {
              return {
                id: element.id,
                text: element.name,
                selected: !!this.value.find((id) => id === element.id),
              };
            }),
        };
      })
    );
    return {
      selectedGroupId: selectedKindId,
      groups,
    };
  }

  async loadItems(kind: SubjectKind): Promise<NamedIdentifier[] | null> {
    if (kind === null) {
      return Promise.resolve(null);
    }
    switch (kind.id) {
      case "sales_agencies":
        return CompaniesService.getSalesAgencies();
      case "broadcasters":
        return BroadcastersService.getBroadcasters();
      case "publishers":
        return PublishersService.getPublishers();
      case "market_segments":
        return MarketSegmentsService.getMarketSegments();
    }
  }
}
